<script lang="ts">
	//Hooks
	import { createEventDispatcher, onMount, setContext } from 'svelte';
	// Svelte Store
	import { writable } from 'svelte/store';
	//Formatting
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		Alert,
		Button,
		Container,
		Modal,
		Text,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
	} from '@Shelly/shelly-design-system';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	//Helpers
	import { getWizardItems, prepareToSave } from 'src/helpers/actions/schedules/core/schedule_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { isBluTRV } from 'src/helpers/interface_helpers';
	//Components
	import DayPicker from 'src/components/TimePicker/DayPicker.svelte';
	import SchedulesActions from './SchedulesActions.svelte';
	import SchedulesTimePicker from './DateTimePickers/SchedulesTimePicker.svelte';
	import ScheduleCalendar from './DateTimePickers/ScheduleCalendar.svelte';
	import ModeSelection from './DateTimePickers/ModeSelection.svelte';
	import PreviewSchedules from './PreviewSchedules.svelte';
	//Types
	import type { ActionJob, ScheduleParams } from 'src/types/requests/schedules/schedules_g2';
	import type { Device } from 'src/types/Device';
	import type { Peripheral } from 'src/types/Peripherals';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { deepCopy } from 'src/helpers/utilities';
	import ExitConfirmationModal from 'src/components/ExitConfirmationModal.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let showWizard: boolean,
		schedule: ScheduleParams = null,
		switchPeripheral: Peripheral = null,
		waitForResponse = false,
		device: Device;

	const deviceCopy = { ...device },
		dispatch = createEventDispatcher(),
		wizardItems = getWizardItems(),
		save = async () => {
			errors.weekday.show = false;
			errors.actions.show = false;
			if (schedule.days.length == 0) {
				errors.weekday.show = true;
				activeStep = wizardItems[0];
				return;
			} else if (schedule.calls.length == 0) {
				errors.actions.show = true;
				return;
			} else if (isBluTRV(device.type)) {
				dispatch('save', {
					id: schedule.id,
					data: prepareToSave(schedule),
					old_schedule: prepareToSave(clonedScheduleObject),
				});
				return;
			}
			waitForResponse = true;
			const res: SettingRequest = await gen2Request(
				device,
				`schedule.${schedule.id ? 'update' : 'create'}`,
				prepareToSave(schedule),
			);
			if (res?.isok) {
				showWizard = false;
				dispatch('save');
			}
			waitForResponse = false;
			onSettingsResponse(res, device, {
				success: $format('sched_save_scc_toast', {
					default: 'Schedule saved successfully.',
				}),
			});
		},
		onChangeDays = (e: CustomEvent) => {
			schedule.days = e.detail.map((i: number) => (i = i == 6 ? 0 : i + 1));
			errors.weekday.show = schedule.days.length == 0;
		},
		days = schedule.days.map((i: number) => (i = i == 0 ? 6 : i - 1)),
		title = schedule.id
			? $format('title_edit_schedule', { default: 'Edit schedule' })
			: $format('title_create_schedule', { default: 'Create schedule' }),
		switchAdvancedMode = (isAdvancedModeTimeTab: boolean) => {
			if (!isAdvancedModeTimeTab) {
				schedule.months.selected = false;
				schedule.dates.selected = false;
			}
		};

	let activeStep = wizardItems[0],
		action: ActionJob = null,
		errors = {
			weekday: {
				show: false,
				label: $format('alert_danger_sched_week_days_error', {
					default: 'Select at least one day!',
				}),
			},
			actions: {
				show: false,
				label: $format('alert_danger_sched_no_actions_error', {
					default: "Can't create a schedule with no actions!",
				}),
			},
		},
		windowClientWidth: number,
		showModal = false,
		activeButtonDaysTab = !schedule.dates.any || !schedule.months.any;

	$: isMobile = windowClientWidth < SystemScreenBreakpoints.MOBILE_MAX;
	$: switchAdvancedMode(activeButtonDaysTab);

	onMount(() => (showModal = true));

	let showExitConfirmationModal = false;
	let exitConfirmationModalState = false;
	let clonedScheduleObject = deepCopy(schedule);

	let containersRefsObj = writable({
		scrollContainerRef: null,
		wizardContainerRef: null,
	});

	setContext('weeklySchedulesContainersRefs', containersRefsObj);

	const keyPress = (e) => {
		e.stopImmediatePropagation();
	};
	$: if (showModal) {
		window.addEventListener('keydown', keyPress);
	} else {
		window.removeEventListener('keydown', keyPress);
	}
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

<Modal
	{title}
	{showModal}
	on:close={() => {
		if (JSON.stringify(schedule) !== JSON.stringify(clonedScheduleObject)) {
			showExitConfirmationModal = true;
			exitConfirmationModalState = false;
		} else {
			showModal = false;
			setTimeout(() => (showWizard = false), 300);
		}
	}}
	bind:scrollContainerRef={$containersRefsObj.scrollContainerRef}
	removeScrollPadding
>
	<Wizard
		slot="toolbar"
		activeStepNumber={activeStep.activeIndex}
		stepsTotal={wizardItems.length}
		activeStepTitle={activeStep.title}
	>
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={() => {
					action = null;
					activeStep = wizardItems[i];
				}}
				active={activeStep.activeIndex === i}
				title={item.title}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<div class:flex-overflow-hidden={false} slot="content">
		<WizardContainer
			activeStepIndex={activeStep.activeIndex}
			adaptiveHeight={true}
			bind:wizardContainerRef={$containersRefsObj.wizardContainerRef}
		>
			{#each wizardItems as item, i (i)}
				<WizardContent
					active={activeStep.activeIndex === i}
					class={classMap({
						'pl-0 pr-0': i == 1,
					})}
				>
					{#if i == 0}
						<ModeSelection
							{isMobile}
							bind:activeButtonTab={activeButtonDaysTab}
							infoText={$format('schedule_choose_days_mode_tooltip', {
								default:
									'Choose Simple mode to select day(s) of the week. Switch to Advanced mode for more complex schedules.',
							})}
						/>
						<hr class="mt-l mb-l" />
						<Container padding={'0'}>
							<Text element="h2" class="text-capitalize mb-m">
								{item.subtitle}
							</Text>
							<DayPicker {days} on:change={onChangeDays} />
							{#if errors.weekday.show}
								<Alert class="mb-s" heading={errors.weekday.label} type="danger" />
							{/if}
							{#if activeButtonDaysTab}
								<hr class="mt-l mb-l" />
								<ScheduleCalendar
									type="dates"
									bind:schedule
									{isMobile}
									toggleText={$format('schedule_select_specific_days', {
										default: 'Select specific days of the month',
									})}
									toggleInfoText={$format('schedule_select_specific_days_subtitle', {
										default:
											'When inactive, this function applies to all days of the month.',
									})}
								/>
								<hr class="mt-l mb-l" />
								<ScheduleCalendar
									type="months"
									bind:schedule
									{isMobile}
									toggleText={$format('schedule_select_specific_months', {
										default: 'Select specific months',
									})}
									toggleInfoText={$format('schedule_select_specific_months_subtitle', {
										default: 'When inactive, this function applies to all months.',
									})}
								/>
							{/if}
						</Container>
					{:else if i == 1}
						<SchedulesTimePicker scrollPaddings bind:schedule {isMobile} />
					{:else if i == 2}
						<SchedulesActions
							bind:schedule
							bind:action
							error={errors.actions}
							{switchPeripheral}
							device={deviceCopy}
						/>
					{:else if i == 3}
						<PreviewSchedules activeIndex={i} {schedule} {errors} device={deviceCopy} />
					{/if}
				</WizardContent>
			{/each}
		</WizardContainer>
	</div>
	<div slot="footer">
		{#if (schedule.days.length > 0 && activeButtonDaysTab && activeStep.activeIndex == 0) || (schedule.type == 'advancedTime' && activeStep.activeIndex == 1)}
			<hr class="mb-l mt-md-0 mt-sm-s" />
			<PreviewSchedules {schedule} activeIndex={activeStep.activeIndex} />
		{/if}
		<div class="d-flex justify-content-end">
			{#if activeStep.activeIndex > 0}
				<Button
					class={classMap({
						'min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s': true,
					})}
					icon="fas fa-chevron-left"
					responsiveSize={{
						sm: 'm',
					}}
					variant="secondary"
					on:click={() => {
						if (activeStep.activeIndex > 0) {
							activeStep = wizardItems[activeStep.activeIndex - 1];
						}
					}}
				>
					{$format('button_back', { default: 'Back' })}
				</Button>
			{/if}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				icon={activeStep.activeIndex == wizardItems.length - 1 ? 'fas fa-save' : ''}
				rightIcon={activeStep.activeIndex == wizardItems.length - 1
					? ''
					: 'fas fa-chevron-right'}
				responsiveSize={{
					sm: 'm',
				}}
				disabled={waitForResponse ||
					(activeStep.activeIndex == wizardItems.length - 1 &&
						(schedule.days.length == 0 || schedule.calls.length == 0))}
				loading={waitForResponse}
				on:click={() => {
					if (activeStep.activeIndex == wizardItems.length - 1) {
						save();
					} else {
						activeStep = wizardItems[activeStep.activeIndex + 1];
					}
				}}
			>
				{activeStep.activeIndex == wizardItems.length - 1
					? $format('button_save', { default: 'Save' })
					: $format('button_next', { default: 'Next' })}
			</Button>
		</div>
	</div>
</Modal>

<ExitConfirmationModal
	type={$format('the_schedule', { default: 'the schedule' })}
	{exitConfirmationModalState}
	{showExitConfirmationModal}
	showAddEditModal={showModal}
	on:close={(e) => {
		exitConfirmationModalState = e.detail.exitConfirmationModalState;
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
	}}
	on:confirm={(e) => {
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
		showWizard = e.detail.showAddEditModal;
		showModal = e.detail.showAddEditModal;
	}}
/>
